import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { LocalLink as Link } from '../../components/LocalLink';
import { useLocation, useNavigate } from 'react-router-dom';
import { Search, ChevronDown, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function Header() {
  const { t, i18n } = useTranslation();

  const navigate = useNavigate();
  const location = useLocation();
  const currentLang = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0].toLowerCase();

  const [isOpen, setIsOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  // Automatically close menu & language dropdown on route change
  useEffect(() => {
    setIsOpen(false);
    setIsLangOpen(false);
  }, [location.pathname]);

  const handleLanguageChange = (langCode: string) => {
    i18n.changeLanguage(langCode);
    setIsLangOpen(false);
    
    const supportedLngs = ['en', 'fr', 'ar'];
    const parts = location.pathname.split('/').filter(Boolean);
    if (supportedLngs.includes(parts[0])) {
      parts[0] = langCode;
    } else {
      parts.unshift(langCode);
    }
    navigate(`/${parts.join('/')}${location.search}${location.hash}`, { 
      replace: true,
      state: { preventScrollReset: true } 
    });
  };

  useEffect(() => {
    if (!isLangOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(event.target as Node)) {
        setIsLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isLangOpen]);

  const preloadMenuRoutes = () => {
    import('../../pages/Catalogue');
    import('../../pages/About');
    import('../../pages/Services');
    import('../../pages/Projects');
    import('../../pages/Exhibitions');
    import('../../pages/Contact');
    import('../../pages/Works');
    import('../../pages/Blog');
    import('../../pages/FAQ');
  };

  const toggleMenu = () => {
    setIsOpen((prev) => {
      const next = !prev;
      if (next) preloadMenuRoutes();
      return next;
    });
  };

  const languages = [
    { code: 'en', label: 'EN', country: 'gb' },
    { code: 'fr', label: 'FR', country: 'fr' },
    { code: 'ar', label: 'عربي', country: 'sa' }
  ];

  const menuLinks = [
    { name: t('header.menu.home', 'Home'), path: '/' },
    { name: t('header.menu.about', 'About Us'), path: '/about' },
    { name: t('header.menu.factory', 'Our Factory'), path: '/works' },
    { name: t('header.menu.catalogue', 'Catalogue'), path: '/catalogue' },
    { name: t('header.menu.services', 'OEM / ODM Services'), path: '/services' },
    { name: t('header.menu.projects', 'Projects'), path: '/projects' },
    { name: t('header.menu.exhibitions', 'Exhibitions & Fairs'), path: '/exhibitions' },
    { name: t('header.menu.blog', 'Blog'), path: '/blog' },
    { name: t('header.menu.faq', 'FAQ'), path: '/faq' },
    { name: t('header.menu.contact', 'Contact'), path: '/contact' },
  ];

  return (
    <>
      <header className="fixed top-0 left-0 w-full z-50 px-8 py-6 flex justify-between items-center bg-white md:bg-transparent md:mix-blend-difference text-black md:text-white transition-colors pointer-events-none select-none">
        {/* Logo / Brand Name */}
        <Link 
          to="/" 
          aria-label="Pending Clouds Art - Home"
          className="z-50 flex items-center pointer-events-auto cursor-pointer py-4 -my-4 pr-4"
          onClick={(e) => {
            const currentPath = window.location.pathname;
            if (currentPath === '/' || currentPath === `/${currentLang}` || currentPath === `/${currentLang}/`) {
              e.preventDefault();
              setIsOpen(false);
              if ((window as any).lenis) {
                (window as any).lenis.scrollTo(0);
              } else {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }
          }}
        >
          {/* Mobile Logo (Triangle only) */}
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 416 150" className="block md:hidden h-5 sm:h-6 w-auto pointer-events-none" fill="currentColor">
            <path d="M415.99 2.77 315.33 148.76c-.49.71-1.43.96-2.21.6L.61 2.02C-.42 1.54-.07 0 1.06 0h413.47c1.42 0 2.26 1.6 1.45 2.77Z" />
          </svg>
          {/* Desktop Logo (Full) */}
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1030.06 150" className="hidden md:block h-6 md:h-8 w-auto pointer-events-none" fill="currentColor" data-name="Calque 1">
            <path d="M541.7 37.01h12.65c4.48 0 8.31-.73 11.48-2.2s5.61-3.56 7.31-6.27c1.71-2.71 2.56-5.96 2.56-9.74s-.85-7.04-2.56-9.78c-1.71-2.73-4.15-4.82-7.31-6.27-3.17-1.44-7-2.16-11.48-2.16h-19.97v52.24h7.31V37.01Zm23.08-26.86c2.41 2.04 3.62 4.93 3.62 8.66s-1.21 6.62-3.62 8.66-5.96 3.06-10.64 3.06h-12.43V7.09h12.43c4.68 0 8.23 1.02 10.64 3.06M595.02 29.48h25.66v-6.35h-25.66V7.09h28.81V.6H587.7v52.23h37.15v-6.49h-29.83zM674.65 39.52 643.94.6h-6v52.23h7.32V13.91l30.71 38.92h6V.6h-7.32zM734.58 3.88C730.41 1.69 725.6.6 720.14.6h-21.57v52.24h21.57c5.46 0 10.28-1.09 14.44-3.28q6.255-3.285 9.72-9.18c2.32-3.93 3.47-8.48 3.47-13.66s-1.16-9.73-3.47-13.66-5.56-6.99-9.72-9.18m3.33 33.09q-2.565 4.44-7.2 6.9c-3.1 1.64-6.76 2.46-11.01 2.46h-13.82V7.09h13.82q6.36 0 11.01 2.46c3.09 1.64 5.5 3.93 7.2 6.87 1.71 2.94 2.56 6.37 2.56 10.3s-.85 7.3-2.56 10.26ZM760.22.6h7.31v52.24h-7.31zM791.44 13.91l30.72 38.92h5.99V.6h-7.31v38.92L790.13.6h-6v52.23h7.31zM547.41 108.88c1.8-1.79 3.94-3.18 6.4-4.18 2.46-.99 5.13-1.49 8.01-1.49s5.39.51 7.83 1.53 4.63 2.58 6.58 4.66l4.75-4.7c-2.34-2.69-5.18-4.71-8.52-6.08s-6.99-2.05-10.93-2.05-7.5.66-10.79 1.98-6.16 3.18-8.59 5.6-4.34 5.24-5.7 8.47c-1.37 3.24-2.05 6.79-2.05 10.67s.68 7.44 2.05 10.67c1.36 3.23 3.25 6.06 5.67 8.47 2.41 2.42 5.27 4.28 8.56 5.6s6.89 1.98 10.79 1.98 7.67-.69 11.01-2.09q5.01-2.085 8.52-6.12l-4.75-4.7c-1.95 2.14-4.14 3.72-6.58 4.74q-3.66 1.53-7.83 1.53c-4.17 0-5.54-.5-8.01-1.49-2.46-.99-4.6-2.39-6.4-4.18s-3.21-3.92-4.21-6.38-1.5-5.14-1.5-8.02.5-5.56 1.5-8.02 2.4-4.59 4.21-6.38ZM599.26 97.17h-7.32v52.23h35.03v-6.49h-27.71zM678.24 104.15c-2.44-2.41-5.31-4.28-8.63-5.6s-6.92-1.98-10.82-1.98-7.58.67-10.89 2.01c-3.32 1.34-6.19 3.22-8.63 5.63s-4.34 5.24-5.7 8.47c-1.37 3.23-2.05 6.77-2.05 10.6s.68 7.36 2.05 10.6c1.37 3.23 3.27 6.06 5.7 8.47 2.44 2.41 5.33 4.29 8.67 5.63q5.01 2.01 10.86 2.01c5.85 0 7.51-.66 10.82-1.98 3.32-1.32 6.19-3.18 8.63-5.6 2.44-2.41 4.33-5.23 5.67-8.47 1.34-3.23 2.01-6.79 2.01-10.67s-.67-7.38-2.01-10.63c-1.34-3.26-3.23-6.09-5.67-8.51Zm-1.14 27.12c-1 2.44-2.39 4.57-4.17 6.38-1.78 1.82-3.88 3.22-6.29 4.22s-5.06 1.49-7.93 1.49-5.45-.5-7.86-1.49-4.52-2.4-6.33-4.22c-1.8-1.82-3.21-3.94-4.21-6.38-1-2.43-1.5-5.1-1.5-7.98s.5-5.53 1.5-7.95c1-2.41 2.4-4.54 4.21-6.38 1.8-1.84 3.91-3.26 6.33-4.25 2.41-.99 5.03-1.49 7.86-1.49s5.52.5 7.93 1.49c2.41 1 4.51 2.41 6.29 4.25s3.17 3.97 4.17 6.38q1.5 3.615 1.5 7.95c0 4.335-.5 5.55-1.5 7.98M734.11 126.57c0 5.82-1.26 10.07-3.77 12.76s-6.08 4.03-10.71 4.03-8.13-1.34-10.64-4.03-3.77-6.94-3.77-12.76v-29.4h-7.31v29.7c0 7.56 1.92 13.31 5.78 17.24 3.85 3.93 9.14 5.89 15.87 5.89s12.08-1.96 15.91-5.89 5.74-9.68 5.74-17.24v-29.7h-7.09v29.4ZM793.46 100.45c-4.17-2.19-8.98-3.28-14.44-3.28h-21.57v52.24h21.57c5.46 0 10.28-1.1 14.44-3.28q6.255-3.285 9.72-9.18c2.32-3.93 3.47-8.48 3.47-13.66s-1.16-9.72-3.47-13.66c-2.31-3.93-5.56-6.99-9.72-9.18m3.32 33.1q-2.565 4.44-7.2 6.9c-3.1 1.64-6.76 2.46-11.01 2.46h-13.82v-39.25h13.82q6.36 0 11.01 2.46c3.09 1.64 5.5 3.93 7.2 6.87 1.71 2.93 2.56 6.37 2.56 10.3s-.85 7.3-2.56 10.26M847.71 124.26c-1.83-1.05-3.85-1.89-6.07-2.54s-4.42-1.23-6.62-1.75c-2.19-.52-4.21-1.11-6.03-1.75s-3.32-1.52-4.46-2.61-1.72-2.56-1.72-4.4c0-1.54.43-2.93 1.28-4.18.85-1.24 2.21-2.24 4.06-2.98 1.85-.75 4.22-1.12 7.09-1.12 2.29 0 4.66.34 7.09 1.01 2.44.67 4.75 1.76 6.95 3.25l2.41-5.97c-2.15-1.49-4.68-2.64-7.61-3.43-2.92-.8-5.87-1.19-8.85-1.19-4.49 0-8.18.68-11.08 2.05s-5.05 3.19-6.44 5.45-2.08 4.74-2.08 7.42.56 5.06 1.68 6.83 2.6 3.17 4.42 4.22c1.83 1.04 3.85 1.9 6.07 2.57s4.42 1.26 6.62 1.75c2.19.5 4.21 1.08 6.03 1.75 1.83.67 3.31 1.56 4.46 2.65 1.14 1.1 1.72 2.54 1.72 4.33q0 2.31-1.32 4.14c-.88 1.22-2.24 2.18-4.1 2.87-1.85.7-4.27 1.05-7.24 1.05-3.27 0-6.41-.57-9.43-1.72-3.02-1.14-5.51-2.58-7.46-4.33l-2.7 5.82c2.05 1.94 4.85 3.52 8.41 4.74q5.34 1.83 11.19 1.83c4.48 0 8.2-.68 11.15-2.05s5.13-3.18 6.55-5.45c1.41-2.26 2.12-4.71 2.12-7.35s-.56-4.97-1.68-6.72c-1.12-1.74-2.6-3.13-4.43-4.18ZM902.09 97.17l-23.18 52.24h7.53l5.62-13.06h27.22l5.62 13.06h7.68l-23.26-52.24h-7.24Zm-7.46 33.21 11.04-25.63 11.04 25.63zM972.88 131.34c3.17-1.44 5.61-3.52 7.31-6.23 1.71-2.71 2.56-5.96 2.56-9.74 0-5.72-1.9-10.19-5.7-13.4s-9.02-4.81-15.65-4.81h-19.96v52.24h7.31v-15.9h12.65c1.13 0 2.21-.05 3.25-.14l11.01 16.03h7.97l-12.17-17.52c.48-.18.97-.33 1.42-.54Zm-11.7-4.17h-12.43v-23.51h12.43c4.68 0 8.23 1.02 10.64 3.06s3.62 4.93 3.62 8.66-1.21 6.63-3.62 8.69-5.96 3.1-10.64 3.1M987.72 97.17v6.49h17.55v45.74h7.24v-45.74h17.55v-6.49zM415.99 2.77 315.33 148.76c-.49.71-1.43.96-2.21.6L.61 2.02C-.42 1.54-.07 0 1.06 0h413.47c1.42 0 2.26 1.6 1.45 2.77ZM868.44 31.04h11.78v12.35c-1.32.88-2.69 1.6-4.13 2.1-2.51.87-5.13 1.31-7.86 1.31-2.93 0-5.63-.5-8.12-1.49s-4.64-2.4-6.47-4.22-3.24-3.96-4.24-6.42q-1.5-3.69-1.5-7.95c0-4.26.5-5.56 1.5-8.02s2.41-4.59 4.24-6.38 4-3.18 6.51-4.18c2.51-.99 5.23-1.49 8.15-1.49s5.54.48 8.01 1.46q3.69 1.455 6.84 4.59l4.54-4.63c-2.34-2.69-5.19-4.7-8.56-6.04S872.06.02 868.01.02 860.35.68 857.04 2c-3.32 1.32-6.21 3.18-8.67 5.6-2.46 2.41-4.37 5.24-5.74 8.47-1.37 3.24-2.05 6.79-2.05 10.67s.68 7.44 2.05 10.67 3.27 6.06 5.7 8.47c2.44 2.41 5.31 4.28 8.63 5.6 3.31 1.32 6.95 1.98 10.89 1.98q5.415 0 10.5-1.68c3.39-1.12 6.35-2.77 8.88-4.96V24.1h-18.8v6.97Z" />
          </svg>
        </Link>

        {/* Right side icons */}
        <div
          className="flex items-center gap-2 md:gap-4 z-50 pointer-events-auto"
          onMouseEnter={preloadMenuRoutes}
        >
          {/* Search Icon */}
          <Link to="/catalogue" className="px-3 py-4 -my-4 hover:opacity-70 transition-opacity cursor-pointer flex items-center justify-center" aria-label="Search Catalogue">
            <Search size={20} strokeWidth={1.5} className="pointer-events-none" />
          </Link>

          {/* Language Selector */}
          <div 
            className="relative" 
            ref={langRef}
          >
            <button 
              type="button"
              onClick={() => setIsLangOpen((prev) => !prev)}
              className="px-3 py-4 -my-4 flex items-center gap-2 text-sm font-thin tracking-widest hover:opacity-70 transition-opacity uppercase cursor-pointer"
            >
              <Globe size={16} strokeWidth={1.5} className="pointer-events-none" />
              <span className="pointer-events-none">{languages.find(l => l.code === currentLang)?.label || 'EN'}</span>
              <ChevronDown size={14} className={`pointer-events-none transition-transform duration-300 ${isLangOpen ? 'rotate-180' : ''}`} />
            </button>
            <AnimatePresence>
              {isLangOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.15 }}
                  className="absolute top-full right-0 mt-4 py-3 px-4 flex flex-col gap-3 text-right items-end"
                >
                  {languages.map((lang) => (
                    <button
                      type="button"
                      key={lang.code}
                      onClick={() => handleLanguageChange(lang.code)}
                      className={`py-1.5 px-3 flex items-center gap-2 text-sm tracking-widest hover:opacity-70 transition-opacity whitespace-nowrap cursor-pointer ${currentLang === lang.code ? 'opacity-100 font-normal' : 'opacity-60 font-thin'}`}
                    >
                      <span className="pointer-events-none">{lang.label}</span>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Menu Button */}
          <button
            type="button"
            onClick={toggleMenu}
            className="px-4 py-4 -my-4 -mr-4 flex items-center group focus:outline-none cursor-pointer"
            aria-label="Toggle Menu"
          >
            {/* Desktop Menu Text */}
            <span className="hidden md:block text-sm font-thin tracking-widest group-hover:opacity-70 transition-opacity uppercase pointer-events-none select-none">
              {isOpen ? t('header.menu.close', 'Close') : t('header.menu.open', 'Menu')}
            </span>

            {/* Mobile 1px Burger Menu Button (3 lines) */}
            <div className="md:hidden relative w-8 h-4 flex flex-col justify-between items-end pointer-events-none">
              <motion.span
                animate={{
                  rotate: isOpen ? 45 : 0,
                  y: isOpen ? 8 : 0,
                }}
                transition={{ duration: 0.25, ease: 'easeInOut' }}
                className="block w-full h-[1px] bg-current origin-center"
              />
              <motion.span
                animate={{
                  opacity: isOpen ? 0 : 1,
                  width: isOpen ? 0 : '70%',
                }}
                transition={{ duration: 0.25, ease: 'easeInOut' }}
                className="block h-[1px] bg-current group-hover:w-full transition-all duration-300"
              />
              <motion.span
                animate={{
                  width: isOpen ? '100%' : '50%',
                  rotate: isOpen ? -45 : 0,
                  y: isOpen ? -7 : 0,
                }}
                transition={{ duration: 0.25, ease: 'easeInOut' }}
                className="block h-[1px] bg-current origin-center group-hover:w-full transition-all duration-300"
              />
            </div>
          </button>
        </div>
      </header>

      {/* Fullscreen Overlay Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: '-100%' }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: '-100%', pointerEvents: 'none' }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-40 bg-spring-bg flex flex-col justify-center items-center"
          >
            <nav className="flex flex-col items-center gap-5 md:gap-6">
              {menuLinks.map((link, index) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 + index * 0.025, duration: 0.3 }}
                >
                  <Link
                    to={link.path}
                    onClick={() => setIsOpen(false)}
                    className="text-2xl md:text-4xl font-light text-spring-text hover:text-spring-accent3 transition-colors duration-300 cursor-pointer"
                  >
                    {link.name}
                  </Link>
                </motion.div>
              ))}
            </nav>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.3 }}
              className="absolute bottom-12 flex flex-col items-center gap-6"
            >
              <div className="flex items-center gap-8 text-sm tracking-widest uppercase text-spring-text/80">
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="hover:text-spring-accent3 hover:opacity-100 transition-colors">{t('header.social.instagram', 'Instagram')}</a>
                <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="hover:text-spring-accent3 hover:opacity-100 transition-colors">{t('header.social.linkedin', 'LinkedIn')}</a>
                <a href="https://pinterest.com" target="_blank" rel="noopener noreferrer" className="hover:text-spring-accent3 hover:opacity-100 transition-colors">{t('header.social.pinterest', 'Pinterest')}</a>
              </div>
              <div className="text-xs tracking-widest text-spring-text/40 uppercase">
                {t('header.slogan', 'Art Beyond Boundaries')}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
